const QUERY_RAIL_SELECTOR = ".fishy-applied-query-rail";

function queryRailForPointer(host, event) {
  if (event?.target?.closest?.("button, a, input, select, textarea, [contenteditable]")) {
    return null;
  }
  const rail = event?.target?.closest?.(QUERY_RAIL_SELECTOR) || null;
  return rail && host?.contains?.(rail) ? rail : null;
}

function canPanHorizontally(element) {
  return Number(element?.scrollWidth || 0) > Number(element?.clientWidth || 0) + 1;
}

function setPanningState(host, scrollElement, active) {
  for (const element of [host, scrollElement]) {
    if (!element?.dataset) {
      continue;
    }
    if (active) {
      element.dataset.panning = "true";
    } else {
      delete element.dataset.panning;
    }
  }
}

export function createQueryScrollController({
  host,
  scrollElement,
  input,
  onCaretEdgeRevealRequested,
}) {
  if (
    typeof host?.addEventListener !== "function" ||
    typeof host?.removeEventListener !== "function" ||
    typeof input?.addEventListener !== "function" ||
    typeof input?.removeEventListener !== "function"
  ) {
    throw new TypeError("Query scrolling requires a rail host and native input");
  }

  let activePan = null;
  const revealCaretEdge = () => {
    if (input.selectionStart !== input.selectionEnd) {
      return false;
    }
    const caret = Number(input.selectionEnd);
    let target = null;
    if (caret === 0) {
      target = 0;
    } else if (caret === String(input.value || "").length) {
      target = Math.max(
        0,
        Number(scrollElement.scrollWidth || 0) - Number(scrollElement.clientWidth || 0),
      );
    }
    if (
      target === null ||
      Math.abs(Number(scrollElement.scrollLeft || 0) - target) <= 0.5
    ) {
      return false;
    }
    scrollElement.scrollLeft = target;
    return true;
  };
  const handleSelectionChange = () => {
    if (typeof onCaretEdgeRevealRequested === "function") {
      onCaretEdgeRevealRequested();
      return;
    }
    revealCaretEdge();
  };

  const finishPan = (event, releaseCapture) => {
    if (!activePan || event?.pointerId !== activePan.pointerId) {
      return false;
    }
    const pointerId = activePan.pointerId;
    activePan = null;
    setPanningState(host, scrollElement, false);
    if (
      releaseCapture &&
      host.hasPointerCapture?.(pointerId) === true
    ) {
      host.releasePointerCapture(pointerId);
    }
    return true;
  };

  const handlePointerDown = (event) => {
    if (
      activePan ||
      event?.button !== 0 ||
      event?.isPrimary === false ||
      !canPanHorizontally(scrollElement)
    ) {
      return;
    }
    const rail = queryRailForPointer(host, event);
    if (!rail) {
      return;
    }
    activePan = {
      pointerId: event.pointerId,
      startClientX: Number(event.clientX || 0),
      startScrollLeft: Number(scrollElement.scrollLeft || 0),
    };
    host.setPointerCapture?.(event.pointerId);
    setPanningState(host, scrollElement, true);
    event.preventDefault?.();
  };

  const handlePointerMove = (event) => {
    if (!activePan || event?.pointerId !== activePan.pointerId) {
      return;
    }
    const distance = Number(event.clientX || 0) - activePan.startClientX;
    scrollElement.scrollLeft = activePan.startScrollLeft - distance;
    event.preventDefault?.();
  };

  const handlePointerUp = (event) => {
    if (finishPan(event, true)) {
      event.preventDefault?.();
    }
  };

  const handlePointerCancel = (event) => {
    finishPan(event, true);
  };

  const handleLostPointerCapture = (event) => {
    finishPan(event, false);
  };

  host.addEventListener("pointerdown", handlePointerDown);
  host.addEventListener("pointermove", handlePointerMove);
  host.addEventListener("pointerup", handlePointerUp);
  host.addEventListener("pointercancel", handlePointerCancel);
  host.addEventListener("lostpointercapture", handleLostPointerCapture);
  input.addEventListener("selectionchange", handleSelectionChange);

  return {
    revealCaretEdge,
    dispose() {
      host.removeEventListener("pointerdown", handlePointerDown);
      host.removeEventListener("pointermove", handlePointerMove);
      host.removeEventListener("pointerup", handlePointerUp);
      host.removeEventListener("pointercancel", handlePointerCancel);
      host.removeEventListener("lostpointercapture", handleLostPointerCapture);
      input.removeEventListener("selectionchange", handleSelectionChange);
      if (
        activePan &&
        host.hasPointerCapture?.(activePan.pointerId) === true
      ) {
        host.releasePointerCapture(activePan.pointerId);
      }
      activePan = null;
      setPanningState(host, scrollElement, false);
    },
  };
}
